/**
 * Client ของ MFA (D-044, D-156) — login step, enrolment และ self-service
 *
 * `mfa_token` เป็น challenge ไม่ใช่ session: ส่งได้เฉพาะใน **body** ของ
 * `/v1/auth/login/mfa*` เท่านั้น ห้ามเป็น Authorization และห้ามเขียนลง storage/log
 * ส่วน `/v1/me/mfa*` ใช้ bearer ของ session เต็มสิทธิ์เท่านั้น
 */

import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import type { DeviceKind } from './client';

export const LOGIN_MFA_PATH = '/v1/auth/login/mfa';
export const LOGIN_MFA_ENROL_START_PATH = '/v1/auth/login/mfa/enrol/start';
export const LOGIN_MFA_ENROL_CONFIRM_PATH = '/v1/auth/login/mfa/enrol/confirm';
export const ME_MFA_PATH = '/v1/me/mfa';
export const ME_MFA_ENROL_START_PATH = '/v1/me/mfa/enrol/start';
export const ME_MFA_ENROL_CONFIRM_PATH = '/v1/me/mfa/enrol/confirm';
export const ME_MFA_RECOVERY_CODES_PATH = '/v1/me/mfa/recovery-codes';

export interface MfaRequestOptions {
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export type MfaFailure =
  /** 401 — รหัสผิด / challenge หมดอายุหรือถูกใช้แล้ว / ถูกล็อก (เซิร์ฟเวอร์ตอบรูปเดียวกันหมด) หรือ session หมด */
  | 'rejected'
  | 'code-invalid'
  | 'current-code-required'
  | 'one-code-required'
  | 'unavailable'
  | 'bad-request'
  | 'server-error'
  | 'network-error'
  | 'invalid-response';

export type MfaResult<T> =
  | { readonly status: 'ok'; readonly value: T }
  | { readonly status: 'failed'; readonly failure: MfaFailure };

export interface MfaSession {
  readonly token: string;
  readonly expiresAt: string | null;
}

export interface MfaEnrolmentStart {
  readonly secret: string;
  readonly otpauthUri: string;
  readonly qrSvg: string;
}

export interface MfaEnrolmentDone {
  readonly recoveryCodes: readonly string[];
}

export interface MfaEnrolmentLoginDone extends MfaEnrolmentDone {
  readonly session: MfaSession;
}

export interface MfaStatus {
  readonly status: string | null;
  readonly enrolledAt: string | null;
  readonly recoveryCodesRemaining: number;
  readonly locked: boolean;
  readonly lockedUntil: string | null;
}

type Json = Record<string, unknown>;

async function call(
  method: 'GET' | 'POST',
  path: string,
  options: MfaRequestOptions,
  body?: Json,
  bearer?: string,
): Promise<MfaResult<Json>> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'failed', failure: 'network-error' };
  }
  const url = buildApiUrl(options.apiBaseUrl ?? getConfiguredApiBaseUrl(), path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(bearer === undefined ? {} : { Authorization: `Bearer ${bearer}` }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    return { status: 'failed', failure: 'network-error' };
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (response.ok) {
    return typeof payload === 'object' && payload !== null
      ? { status: 'ok', value: payload as Json }
      : { status: 'failed', failure: 'invalid-response' };
  }

  if (response.status === 401) {
    return { status: 'failed', failure: 'rejected' };
  }
  if (response.status === 400) {
    return { status: 'failed', failure: 'bad-request' };
  }
  if (response.status === 503) {
    return { status: 'failed', failure: 'unavailable' };
  }
  if (response.status === 422) {
    const details = (payload as { details?: { rule?: unknown } } | null)?.details;
    const rule = typeof details?.rule === 'string' ? details.rule : '';
    if (rule === 'MFA_CODE_INVALID') {
      return { status: 'failed', failure: 'code-invalid' };
    }
    if (rule === 'MFA_CURRENT_CODE_REQUIRED') {
      return { status: 'failed', failure: 'current-code-required' };
    }
    if (rule === 'MFA_ONE_CODE_REQUIRED') {
      return { status: 'failed', failure: 'one-code-required' };
    }
  }
  return { status: 'failed', failure: 'server-error' };
}

function str(record: Json, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function codesOf(record: Json): string[] | null {
  const value = record.recovery_codes;
  if (!Array.isArray(value) || value.length === 0 || !value.every((c) => typeof c === 'string')) {
    return null;
  }
  return value as string[];
}

function sessionOf(record: Json): MfaSession | null {
  const token = str(record, 'token');
  if (token === null) {
    return null;
  }
  return { token: token.trim(), expiresAt: str(record, 'expires_at') };
}

const INVALID: MfaResult<never> = { status: 'failed', failure: 'invalid-response' };

function startOf(record: Json): MfaResult<MfaEnrolmentStart> {
  const secret = str(record, 'secret');
  const otpauthUri = str(record, 'otpauth_uri');
  const qrSvg = str(record, 'qr_svg');
  if (secret === null || otpauthUri === null || qrSvg === null) {
    return INVALID;
  }
  return { status: 'ok', value: { secret, otpauthUri, qrSvg } };
}

export interface LoginMfaInput {
  readonly mfaToken: string;
  readonly deviceKind: DeviceKind;
  readonly code?: string;
  readonly recoveryCode?: string;
}

export async function submitLoginMfa(
  input: LoginMfaInput,
  options: MfaRequestOptions = {},
): Promise<MfaResult<MfaSession>> {
  const result = await call(
    'POST',
    LOGIN_MFA_PATH,
    options,
    {
      mfa_token: input.mfaToken,
      device_kind: input.deviceKind,
      ...(input.code === undefined ? {} : { code: input.code }),
      ...(input.recoveryCode === undefined ? {} : { recovery_code: input.recoveryCode }),
    },
  );
  if (result.status === 'failed') {
    return result;
  }
  const session = sessionOf(result.value);
  return session === null ? INVALID : { status: 'ok', value: session };
}

export async function startLoginEnrolment(
  mfaToken: string,
  options: MfaRequestOptions = {},
): Promise<MfaResult<MfaEnrolmentStart>> {
  const result = await call('POST', LOGIN_MFA_ENROL_START_PATH, options, { mfa_token: mfaToken });
  return result.status === 'failed' ? result : startOf(result.value);
}

export async function confirmLoginEnrolment(
  input: { readonly mfaToken: string; readonly code: string; readonly deviceKind: DeviceKind },
  options: MfaRequestOptions = {},
): Promise<MfaResult<MfaEnrolmentLoginDone>> {
  const result = await call('POST', LOGIN_MFA_ENROL_CONFIRM_PATH, options, {
    mfa_token: input.mfaToken,
    code: input.code,
    device_kind: input.deviceKind,
  });
  if (result.status === 'failed') {
    return result;
  }
  const session = sessionOf(result.value);
  const recoveryCodes = codesOf(result.value);
  return session === null || recoveryCodes === null
    ? INVALID
    : { status: 'ok', value: { session, recoveryCodes } };
}

export async function readMyMfa(
  bearer: string,
  options: MfaRequestOptions = {},
): Promise<MfaResult<MfaStatus>> {
  const result = await call('GET', ME_MFA_PATH, options, undefined, bearer);
  if (result.status === 'failed') {
    return result;
  }
  const r = result.value;
  return {
    status: 'ok',
    value: {
      status: str(r, 'status'),
      enrolledAt: str(r, 'enrolled_at'),
      recoveryCodesRemaining: typeof r.recovery_codes_remaining === 'number' ? r.recovery_codes_remaining : 0,
      locked: r.locked === true,
      lockedUntil: str(r, 'locked_until'),
    },
  };
}

export async function startMyEnrolment(
  bearer: string,
  currentCode: string | undefined,
  options: MfaRequestOptions = {},
): Promise<MfaResult<MfaEnrolmentStart>> {
  const result = await call(
    'POST',
    ME_MFA_ENROL_START_PATH,
    options,
    currentCode === undefined ? {} : { current_code: currentCode },
    bearer,
  );
  return result.status === 'failed' ? result : startOf(result.value);
}

export async function confirmMyEnrolment(
  bearer: string,
  code: string,
  options: MfaRequestOptions = {},
): Promise<MfaResult<MfaEnrolmentDone>> {
  const result = await call('POST', ME_MFA_ENROL_CONFIRM_PATH, options, { code }, bearer);
  if (result.status === 'failed') {
    return result;
  }
  const recoveryCodes = codesOf(result.value);
  return recoveryCodes === null ? INVALID : { status: 'ok', value: { recoveryCodes } };
}

export async function regenerateMyRecoveryCodes(
  bearer: string,
  currentCode: string,
  options: MfaRequestOptions = {},
): Promise<MfaResult<MfaEnrolmentDone>> {
  const result = await call(
    'POST',
    ME_MFA_RECOVERY_CODES_PATH,
    options,
    { current_code: currentCode },
    bearer,
  );
  if (result.status === 'failed') {
    return result;
  }
  const recoveryCodes = codesOf(result.value);
  return recoveryCodes === null ? INVALID : { status: 'ok', value: { recoveryCodes } };
}
